import React from 'react'

import { faPlusCircle } from '@fortawesome/pro-light-svg-icons'
import { faRedo } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@material-ui/core'
import { lighten, withStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
import localwindow from 'browser-lib/window'
import { findIndex, get, isEmpty, isEqual, sortBy } from 'lodash'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import api from 'common/api'
import { actions as linkedCalendarsActions } from 'store/linked-calendars'
import { actions as userActions } from 'store/user'

import { external } from '../../visit-colors'
import CalendarSourceModal from './CalendarSourceModal'
import Disconnect from './Disconnect'
import HelpInfo from './HelpInfo'
import PreferredCalendarsModal from './PreferredCalendarsModal'
import ScoirLogo from './ScoirLogo'

const TheCheckBox = (props) => (
  <Checkbox
    color="default"
    {...props}
  />
)

const colorizer = (color) =>
  withStyles({
    root: {
      color: lighten(color, 0.2),
      '&$checked': {
        color: color,
      },
    },
    checked: {},
  })(TheCheckBox)

const ColoredCheckboxes = external.map((clr) => colorizer(clr))

export class LinkedCalendars extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      hasSyncedCalendar: !isEmpty(get(props, 'calendarConfig.Source')),
      isEditCalendarsOpen: false,
      isCalendarSourceOpen: false,
      isDeleteCalendarSyncOpen: false,
      availableCalendars: [],
    }
  }
  editCalendars() {
    api.get(`/api/users/${this.props.userId}/calendars`).then((availableCalendars) => {
      this.setState({
        availableCalendars,
        isEditCalendarsOpen: true,
      })
    })
  }
  setPreferredCalendars(writeCalendar, readCalendars) {
    return api
      .post(`/api/users/${this.props.userId}/calendars/preferred`, {
        Write: writeCalendar,
        Read: readCalendars,
      })
      .then(() => {
        this.props.fetchUser()
        this.setState({
          isEditCalendarsOpen: false,
        })
      })
  }
  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps, this.props)) {
      if (!this.state.hasSyncedCalendar && !isEmpty(get(this.props, 'calendarConfig.Source'))) {
        this.setState({
          hasSyncedCalendar: true,
        })
      }
    }
  }
  componentDidMount() {
    localwindow.addEventListener('message', (message) => {
      if (message.data.key === 'scoir.oauthResult' && message.data.success) {
        this.setState({
          hasSyncedCalendar: true,
        })
        this.editCalendars()
      }
    })
  }
  toggleLinkedCalendar(cal) {
    if (cal.visible) {
      this.props.hideLinkedCalendar(cal)
    } else {
      this.props.showLinkedCalendar(cal)
    }
  }
  removeCalendarCredentials() {
    api.put('/api/oauth2/calendar/revoke').then(() => {
      this.props.fetchUser()
      this.setState({
        isDeleteCalendarSyncOpen: false,
        hasSyncedCalendar: false,
      })
    })
  }
  renderLinkedCalendars() {
    const getIdx = (cal) => findIndex(this.props.calendars, { id: cal.id })
    const sortedLinkedCalendars = sortBy(this.props.calendars, (cal) => cal.label.toLowerCase())
    if (this.props.calendars.length > 0) {
      return (
        <Box py={1}>
          {sortedLinkedCalendars.map((cal) => {
            const ColoredCheckbox = ColoredCheckboxes[getIdx(cal)]
            return (
              <Box key={cal.id}>
                <FormControlLabel
                  label={cal.label}
                  control={
                    <ColoredCheckbox
                      checked={cal.visible}
                      disabled={this.state.hasSyncedCalendar && this.props.calendarError}
                      onChange={() => this.toggleLinkedCalendar(cal)}
                    />
                  }
                />
              </Box>
            )
          })}
        </Box>
      )
    }
    return null
  }
  render() {
    return (
      <div>
        <div>
          <Box
            display="flex"
            alignItems="center"
          >
            <Typography
              variant="body2"
              color="textSecondary"
            >
              Linked Calendars
            </Typography>
            <HelpInfo />
            {this.state.hasSyncedCalendar && (
              <Box justifySelf="flex-end">
                <Button
                  color="primary"
                  onClick={() => this.editCalendars()}
                >
                  Edit
                </Button>
              </Box>
            )}
          </Box>
          {this.renderLinkedCalendars()}
          {get(this.props.calendarConfig, 'WriteCalendar.Label') && (
            <Box mt={1}>
              <Typography
                variant="body2"
                component="span"
              >
                <em>Sharing</em>
              </Typography>
              <Box
                color="primary.main"
                component="span"
              >
                {' '}
                <ScoirLogo style={{ height: '1.4em', verticalAlign: 'middle' }} />{' '}
              </Box>
              <Typography
                variant="body2"
                component="span"
              >
                <em>Visits with {this.props.calendarConfig.WriteCalendar.Label}</em>
              </Typography>
            </Box>
          )}
        </div>
        <Box>
          {this.state.hasSyncedCalendar ? (
            <React.Fragment>
              {this.props.calendarError && (
                <>
                  <Box m={1}>
                    <Alert
                      severity="error"
                      icon={false}
                    >
                      We had trouble connecting with your calendar. Your sync has been disconnected. Simply Re-authorize
                      your calendar account.
                    </Alert>
                  </Box>
                  <Box mt={1}>
                    <Button
                      color="primary"
                      onClick={() => {
                        this.setState({ isCalendarSourceOpen: true })
                      }}
                      startIcon={<FontAwesomeIcon icon={faRedo} />}
                    >
                      Re-authorize
                    </Button>
                  </Box>
                </>
              )}
              <Box mt={1}>
                <Disconnect
                  onComplete={() => {
                    this.setState({ hasSyncedCalendar: false })
                  }}
                />
              </Box>
            </React.Fragment>
          ) : (
            <Button
              color="primary"
              size="small"
              onClick={() => {
                this.setState({ isCalendarSourceOpen: true })
              }}
            >
              <FontAwesomeIcon icon={faPlusCircle} />
              &nbsp; Link an external calendar
            </Button>
          )}
        </Box>
        <PreferredCalendarsModal
          readCalendars={get(this.state, 'availableCalendars.Read')}
          writeCalendars={get(this.state, 'availableCalendars.Write')}
          onClose={() => {
            this.setState({ isEditCalendarsOpen: false })
          }}
          onSave={(writeCalendar, readCalendars) => this.setPreferredCalendars(writeCalendar, readCalendars)}
          onRemoveCalendarCredentials={() => this.removeCalendarCredentials()}
          isOpen={this.state.isEditCalendarsOpen}
          writeCalendarId={get(this.props.calendarConfig, 'WriteCalendar.Id')}
          viewCalendars={get(this.props.calendarConfig, 'ReadCalendars')}
        />
        <CalendarSourceModal
          onClose={() => {
            this.setState({ isCalendarSourceOpen: false })
          }}
          isOpen={this.state.isCalendarSourceOpen}
        />
      </div>
    )
  }
}

LinkedCalendars.propTypes = {
  userId: PropTypes.string.isRequired,
  calendarConfig: PropTypes.object,
  fetchUser: PropTypes.func.isRequired,
  calendars: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      visible: PropTypes.bool.isRequired,
    }),
  ),
  showLinkedCalendar: PropTypes.func.isRequired,
  hideLinkedCalendar: PropTypes.func.isRequired,
  calendarError: PropTypes.bool,
}

export default connect(
  (state) => ({
    calendars: state.linkedCalendars,
    userId: state.user.record.Id,
    calendarConfig: state.user.record.CalendarConfig,
    calendarError: get(state.user.record, 'CalendarConfig.Disconnected'),
  }),
  {
    fetchUser: userActions.fetchUser,
    showLinkedCalendar: linkedCalendarsActions.showLinkedCalendar,
    hideLinkedCalendar: linkedCalendarsActions.hideLinkedCalendar,
  },
)(LinkedCalendars)
